import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange } from '../../range-validate';
import { SipCalculatorService, SipResult, SIP_CONSTRAINTS } from './sip-calculator.service';

@Component({
  selector: 'app-sip-calculator',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  providers: [InvestmentChartService],
  templateUrl: './sip-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./sip-calculator.scss']
})
export class SipCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(SipCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('growthRef')    growthRef!:    ElementRef<HTMLCanvasElement>;

  readonly C = SIP_CONSTRAINTS;
  readonly Math = Math;

  monthlyInvestment = 10_000;
  annualRate = 12;
  tenureYears = 10;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     SipResult | null = null;
  activeTab:  'yearly' | 'monthly' = 'yearly';

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.monthlyInvestment, this.C.monthlyInvestment.min, this.C.monthlyInvestment.max, 'Monthly investment');
    if (e1) errors['monthlyInvestment'] = e1;
    const e2 = validateRange(this.annualRate, this.C.rate.min, this.C.rate.max, 'Expected return');
    if (e2) errors['annualRate'] = e2;
    const e3 = validateRange(this.tenureYears, this.C.tenureYears.min, this.C.tenureYears.max, 'Tenure');
    if (e3) errors['tenureYears'] = e3;
    else if (!Number.isInteger(Number(this.tenureYears))) errors['tenureYears'] = 'Tenure must be a whole number of years.';
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    if (field === 'tenureYears') this.tenureYears = Math.floor(this.tenureYears || 0);
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = { monthlyInvestment: true, annualRate: true, tenureYears: true };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(this.monthlyInvestment, this.annualRate, Math.floor(this.tenureYears));
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdown(this.breakdownRef.nativeElement, [
        { label: 'Total Invested', value: r.totalInvested, color: '#3b82f6' },
        { label: 'Wealth Gained', value: r.totalGain, color: '#10b981' }
      ], 'Maturity Value');
      this.charts.renderGrowth(
        this.growthRef.nativeElement,
        r.years.map(row => `Yr ${row.year}`),
        [
          { label: 'Invested', data: r.years.map(row => Math.round(row.invested)), color: '#94a3b8', dashed: true },
          { label: 'Corpus Value', data: r.years.map(row => Math.round(row.balance)), color: '#3b82f6' }
        ]
      );
    }, 60);
  }

  reset(): void {
    this.monthlyInvestment = 10_000;
    this.annualRate        = 12;
    this.tenureYears       = 10;
    this.formErrors        = {};
    this.touched           = {};
    this.showFormErrors    = false;
    this.calculated         = false;
    this.result             = null;
    this.activeTab          = 'yearly';
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
